import { useState, useEffect } from 'react';
import { getDiscount, isDiscountCurrentlyActive, type PolarDiscount } from '@/api/polar';
import { CAMPAIGNS, SUBSCRIPTION_TIERS } from '@/api/subscriptions';

interface CampaignStatus {
  [campaignKey: string]: boolean;
}

interface CampaignDiscounts {
  [campaignKey: string]: PolarDiscount | null;
}

/**
 * Hook to check if campaigns are active based on Polar discount status
 * Also fetches actual discount data for accurate pricing
 * Caches results for 5 minutes to avoid excessive API calls
 */
export function useCampaignStatus() {
  const [campaignStatus, setCampaignStatus] = useState<CampaignStatus>({});
  const [campaignDiscounts, setCampaignDiscounts] = useState<CampaignDiscounts>({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const campaignKeysInUse = Array.from(
      new Set(
        SUBSCRIPTION_TIERS
          .map((tier) => tier.campaign)
          .filter((campaignKey): campaignKey is string => Boolean(campaignKey && CAMPAIGNS[campaignKey]))
      )
    );

    const checkCampaigns = async () => {
      setIsLoading(true);
      const status: CampaignStatus = {};
      const discounts: CampaignDiscounts = {};

      if (campaignKeysInUse.length === 0) {
        setCampaignStatus(status);
        setCampaignDiscounts(discounts);
        setIsLoading(false);
        return;
      }

      // Check only campaigns actually referenced by subscription tiers.
      for (const campaignKey of campaignKeysInUse) {
        const campaign = CAMPAIGNS[campaignKey];

        try {
          const discount = await getDiscount(campaign.discount_id);
          status[campaignKey] = isDiscountCurrentlyActive(discount);
          discounts[campaignKey] = discount;
        } catch (error) {
          console.error(`Error checking campaign ${campaignKey}:`, error);
          status[campaignKey] = false;
          discounts[campaignKey] = null;
        }
      }

      setCampaignStatus(status);
      setCampaignDiscounts(discounts);
      setIsLoading(false);
    };

    checkCampaigns();

    // Refresh campaign status every 5 minutes (if campaigns are in use).
    const interval = campaignKeysInUse.length > 0
      ? setInterval(checkCampaigns, 5 * 60 * 1000)
      : null;

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, []);

  /**
   * Check if a specific campaign is active
   */
  const isCampaignActive = (campaignKey: string): boolean => {
    return campaignStatus[campaignKey] ?? false;
  };

  /**
   * Get Polar discount data for a campaign
   */
  const getCampaignDiscount = (campaignKey: string): PolarDiscount | null => {
    return campaignDiscounts[campaignKey] ?? null;
  };

  return {
    campaignStatus,
    campaignDiscounts,
    isCampaignActive,
    getCampaignDiscount,
    isLoading,
  };
}
